import type { HierarchyNodeType, SetupStepId, ThemeMode, ThemePalette } from './models';

export const SETUP_STEPS: readonly { id: SetupStepId; label: string }[] = [
  { id: 'organization-details', label: 'Organization Details' },
  { id: 'site-configuration', label: 'Site Configuration' },
  { id: 'packages', label: 'Packages' },
  /*
    "Entity Setup", not "Set Hierarchy": the reference's names are the
    specification and this step was the only one that did not match (design
    decision 1). The step id stays `set-hierarchy` -- it is the server's stable
    key in the setup-progress response, and renaming a label is not a reason to
    break a contract.
  */
  { id: 'set-hierarchy', label: 'Entity Setup' },
  { id: 'assign-admins', label: 'Assign Admins' },
  { id: 'entity-summary', label: 'Entity Summary' },
];

export const STEP_COUNT = SETUP_STEPS.length;

export const HIERARCHY_STEP_INDEX = 3;

/** One name per step, always. The label no longer changes with the active step. */
export const STEP_LABELS: readonly string[] = SETUP_STEPS.map((step) => step.label);

export const PAGE_HEADING = 'Organization Setup';
export const PAGE_SUBHEADING =
  'Configure your organization, site settings, packages, hierarchy, and administrators.';

/** Section header icon per step, matching the reference's icon tiles. */
export const STEP_SECTION: Record<SetupStepId, { icon: string; title: string }> = {
  'organization-details': { icon: 'pi-building', title: 'Organization Information' },
  'site-configuration': { icon: 'pi-globe', title: 'Site Configuration' },
  packages: { icon: 'pi-box', title: 'Packages' },
  'set-hierarchy': { icon: 'pi-sitemap', title: 'Entity Setup' },
  'assign-admins': { icon: 'pi-users', title: 'Assign Admin' },
  'entity-summary': { icon: 'pi-th-large', title: 'Entity Summary' },
};

/*
  Two different ceilings, not one constant reused: the entity logo is capped at
  500KB by `@Base64Image` on the server and its hint has always said so, while
  the login image is capped at 2MB (`@Base64Image(maxBytes = 2 * 1024 * 1024)`).
  Sharing one 2MB value let the form accept a logo the endpoint would refuse.
*/
export const LOGO_MAX_BYTES = 500 * 1024;
export const LOGIN_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
/** The Login Image's exact size (NB-404): checked on the picked file, and by the server on a new image. */
export const LOGIN_IMAGE_DIMENSIONS = { width: 960, height: 1080 } as const;
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg'];

export const MOCK_LATENCY_MS = 350;

/* -- hierarchy ------------------------------------------------------------- */

/** What each node level can create beneath it. */
export const CHILD_TYPE: Record<HierarchyNodeType, HierarchyNodeType | null> = {
  organization: 'entity',
  entity: 'businessUnit',
  businessUnit: 'division',
  division: 'department',
  department: null,
};

export const NODE_LABEL: Record<HierarchyNodeType, string> = {
  organization: 'Organization',
  entity: 'Entity',
  businessUnit: 'Business Unit',
  division: 'Division',
  department: 'Department',
};

/** Plural noun used in the child-count chip. */
export const NODE_LABEL_PLURAL: Record<HierarchyNodeType, string> = {
  organization: 'Organizations',
  entity: 'Entities',
  businessUnit: 'Business Units',
  division: 'Divisions',
  department: 'Departments',
};

/**
 * The reference tints the count chip by depth: primary at the organization
 * row, blue at entity, green at business unit, grey below that.
 */
export const NODE_CHIP_VARIANT: Record<HierarchyNodeType, string> = {
  organization: 'primary',
  entity: 'blue',
  businessUnit: 'green',
  division: 'grey',
  department: 'grey',
};

export const CODE_PREFIX: Record<HierarchyNodeType, string> = {
  organization: 'ORG',
  entity: 'ENT',
  businessUnit: 'BU',
  division: 'DIV',
  department: 'DEP',
};

/* -- theme dialog ---------------------------------------------------------- */

/**
 * Swatch colours live in organization-setup.css as
 * `.org-setup__theme-swatch--{id}` rather than here, because inline styles are
 * forbidden (organization.md section 5.1). Values came from the reference's
 * color-theme.css.
 */
export const THEME_PALETTES: readonly { id: ThemePalette; label: string }[] = [
  { id: 'default', label: 'Default' },
  { id: 'slate', label: 'Slate' },
  { id: 'red', label: 'Red' },
  { id: 'rose', label: 'Rose' },
  { id: 'brown', label: 'Brown' },
  { id: 'green', label: 'Green' },
  { id: 'yellow', label: 'Yellow' },
];

/**
 * site-config.md section 3 is explicit that the mode list is a fixed backend
 * enum and must NOT be fetched (no call to /site-preference/theme-options).
 * The labels are also what the save endpoint accepts on the wire.
 */
export const THEME_MODES: readonly { id: ThemeMode; label: string; icon: string }[] = [
  { id: 'light', label: 'Light', icon: 'pi-sun' },
  { id: 'dark', label: 'Dark', icon: 'pi-moon' },
];

/** What the Customize panel's Reset restores (section 3). */
export const DEFAULT_SITE_THEME = { palette: 'default', mode: 'light' } as const;

/**
 * The status an organization holds between step 1's create and the setup being completed.
 *
 * Both spellings, because the Submit decision reads both: the id is what `os_organization` stores and the
 * name is what the response resolves it to, and an installation that renumbers its status master still
 * answers "Pending". Either matching means the setup has not been completed yet.
 */
export const PENDING_STATUS_ID = 10;
export const PENDING_STATUS_NAME = 'Pending';

export const TOAST_LIFE_MS = 3000;
